import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Badge, Button, Card, Drawer, Input, InputNumber, message, Popconfirm,
  Select, Space, Steps, Switch, Table, Tabs, Tag, Tooltip, Typography,
} from "antd";
import type { TableProps } from "antd";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";
import ModelCallLogsTab from "../../components/admin/ModelCallLogsTab";

const { Text } = Typography;

interface ChannelModel {
  endpointId: number;
  publicModel: string;
  upstreamModel: string;
  status: string;
  errorCount: number;
  pendingPrice: boolean;
}

interface Channel {
  id: number;
  name: string;
  type: string;
  baseUrl: string;
  keyPreview: string;
  priority: number;
  enabled: boolean;
  models: ChannelModel[] | null;
  modelCount: number;
  enabledCount: number;
}

// 类型卡片:选类型自动填默认 BaseURL(01§3.1 向导第 1 步)
const CHANNEL_TYPES = [
  { value: "openai", label: "OpenAI", baseUrl: "https://api.openai.com/v1" },
  { value: "deepseek", label: "DeepSeek", baseUrl: "https://api.deepseek.com/v1" },
  { value: "anthropic", label: "Anthropic", baseUrl: "https://api.anthropic.com/v1" },
  { value: "zhipu", label: "智谱", baseUrl: "https://open.bigmodel.cn/api/paas/v4" },
  { value: "moonshot", label: "Moonshot", baseUrl: "https://api.moonshot.cn/v1" },
  { value: "qwen", label: "通义千问", baseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1" },
  { value: "doubao", label: "豆包", baseUrl: "https://ark.cn-beijing.volces.com/api/v3" },
  { value: "custom", label: "自定义(OpenAI 兼容)", baseUrl: "" },
];

const ModelGateway: React.FC = () => {
  const { API_BASE_URL } = useGlobal();
  const navigate = useNavigate();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; latencyMs: number; message: string } | null>(null);
  const [upstreamModels, setUpstreamModels] = useState<string[]>([]);
  // 向导表单
  const [form, setForm] = useState({ name: "", type: "openai", baseUrl: CHANNEL_TYPES[0].baseUrl, apiKey: "", priority: 0, models: [] as string[] });

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/admin/channels`);
      if (res.data.code === 200) setChannels(res.data.data || []);
      else message.error(res.data.msg || "获取渠道失败");
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "获取渠道失败");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  const openWizard = () => {
    setForm({ name: "", type: "openai", baseUrl: CHANNEL_TYPES[0].baseUrl, apiKey: "", priority: 0, models: [] });
    setStep(0);
    setTestResult(null);
    setUpstreamModels([]);
    setWizardOpen(true);
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await request.post(`${API_BASE_URL}/api/v3/admin/channels/test`, { baseUrl: form.baseUrl, apiKey: form.apiKey }, { timeout: 30000 });
      if (res.data.code === 200) setTestResult(res.data.data);
      else message.error(res.data.msg || "测试失败");
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "测试失败");
    } finally {
      setTesting(false);
    }
  };

  const fetchModels = async () => {
    setFetching(true);
    try {
      const res = await request.post(`${API_BASE_URL}/api/v3/admin/channels/fetch-models`, { baseUrl: form.baseUrl, apiKey: form.apiKey }, { timeout: 30000 });
      if (res.data.code === 200) {
        const models: string[] = res.data.data?.models || [];
        setUpstreamModels(models);
        if (models.length === 0) message.info("上游未返回模型,请手动输入");
        else message.success(`拉取到 ${models.length} 个模型,请勾选`);
      } else {
        message.warning(res.data.msg || "拉取失败,可手动输入模型名");
      }
    } catch (e: any) {
      message.warning(e?.response?.data?.msg || "拉取失败,可手动输入模型名");
    } finally {
      setFetching(false);
    }
  };

  const createChannel = async () => {
    if (!form.models.length) {
      message.warning("请至少选择或输入一个模型");
      return;
    }
    setSaving(true);
    try {
      const res = await request.post(`${API_BASE_URL}/api/v3/admin/channels`, {
        name: form.name,
        type: form.type,
        baseUrl: form.baseUrl,
        apiKey: form.apiKey,
        priority: form.priority,
        models: form.models.map((m) => ({ public: m, upstream: "" })),
      });
      if (res.data.code === 200) {
        message.success("渠道已创建;新模型已自动进入目录(待定价、未公开),请到模型管理补全价格");
        setWizardOpen(false);
        fetchChannels();
      } else {
        message.error(res.data.msg || "创建失败");
      }
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "创建失败");
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = async (channel: Channel, enabled: boolean) => {
    try {
      const res = await request.patch(`${API_BASE_URL}/api/v3/admin/channels/${channel.id}/status`, { enabled });
      if (res.data.code === 200) fetchChannels();
      else message.error(res.data.msg || "操作失败");
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "操作失败");
    }
  };

  const deleteChannel = async (channel: Channel) => {
    try {
      const res = await request.delete(`${API_BASE_URL}/api/v3/admin/channels/${channel.id}`);
      if (res.data.code === 200) {
        message.success("渠道已删除(模型目录项保留)");
        fetchChannels();
      } else message.error(res.data.msg || "删除失败");
    } catch (e: any) {
      message.error(e?.response?.data?.msg || "删除失败");
    }
  };

  const columns: TableProps<Channel>["columns"] = [
    {
      title: "渠道",
      key: "name",
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Badge status={r.enabled ? "success" : "default"} />
            <Text strong>{r.name}</Text>
            <Tag>{CHANNEL_TYPES.find((t) => t.value === r.type)?.label || r.type}</Tag>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.baseUrl} · {r.keyPreview}</Text>
        </Space>
      ),
    },
    {
      title: "模型",
      key: "models",
      width: 320,
      render: (_, r) => (
        <Space wrap size={4}>
          {(r.models || []).slice(0, 6).map((m) => (
            <Tooltip key={m.endpointId} title={`上游名:${m.upstreamModel}${m.pendingPrice ? " · 待定价" : ""} · 错误 ${m.errorCount} 次`}>
              <Tag color={m.status === "enabled" ? (m.pendingPrice ? "orange" : "blue") : "default"}>
                {m.publicModel}
                {m.pendingPrice ? " ¥?" : ""}
              </Tag>
            </Tooltip>
          ))}
          {(r.models || []).length > 6 && <Tag>+{(r.models || []).length - 6}</Tag>}
        </Space>
      ),
    },
    { title: "优先级", dataIndex: "priority", width: 80 },
    {
      title: "启用",
      width: 80,
      render: (_, r) => <Switch size="small" checked={r.enabled} onChange={(checked) => toggleChannel(r, checked)} />,
    },
    {
      title: "操作",
      width: 100,
      render: (_, r) => (
        <Popconfirm title={`删除渠道「${r.name}」?其下 ${r.modelCount} 个模型端点将停止服务`} okText="删除" cancelText="取消" onConfirm={() => deleteChannel(r)}>
          <Button size="small" danger type="text">删除</Button>
        </Popconfirm>
      ),
    },
  ];

  const stepValid = step === 0 ? !!form.name.trim() && !!form.baseUrl.trim() : step === 1 ? !!form.apiKey.trim() || form.type === "custom" : form.models.length > 0;

  return (
    <div style={{ background: "#f0f2f5", minHeight: "100%", padding: 16 }}>
      <Card
        title={<Space><i className="ri-router-line" style={{ color: "#1677ff", fontSize: 20 }} /><span>模型网关 · 渠道</span></Space>}
        extra={
          <Space>
            <Button onClick={() => navigate("/model-management")}>模型目录(旧版管理)</Button>
            <Button onClick={fetchChannels}>刷新</Button>
            <Button type="primary" onClick={openWizard} icon={<i className="ri-add-line" />}>新建渠道</Button>
          </Space>
        }
      >
        <Tabs
          defaultActiveKey="channels"
          items={[
            {
              key: "channels",
              label: "渠道",
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="渠道 = 一个上游账号(BaseURL + Key + 模型列表)。新建渠道 3 步完成;勾选的新模型自动进入模型目录(待定价、未公开),价格与能力在「模型目录」补全。"
                  />
                  <Table rowKey="id" loading={loading} columns={columns} dataSource={channels} pagination={false}
                    locale={{ emptyText: "暂无渠道,点击右上角「新建渠道」开始接入" }} />
                </>
              ),
            },
            {
              key: "logs",
              label: "调用日志",
              children: <ModelCallLogsTab channels={channels.map((c) => ({ id: c.id, name: c.name }))} />,
            },
          ]}
        />
      </Card>

      <Drawer title="新建渠道" width={520} open={wizardOpen} onClose={() => setWizardOpen(false)}>
        <Steps size="small" current={step} items={[{ title: "类型与地址" }, { title: "密钥与测试" }, { title: "选择模型" }]} style={{ marginBottom: 20 }} />
        {step === 0 && (
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            <div>
              <Text type="secondary">渠道类型</Text>
              <Select
                style={{ width: "100%", marginTop: 6 }}
                value={form.type}
                options={CHANNEL_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                onChange={(value) => {
                  const t = CHANNEL_TYPES.find((item) => item.value === value);
                  setForm((f) => ({ ...f, type: value, baseUrl: t?.baseUrl || f.baseUrl }));
                }}
              />
            </div>
            <div>
              <Text type="secondary">渠道名称</Text>
              <Input style={{ marginTop: 6 }} value={form.name} placeholder="例如:OpenAI 主账号" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Text type="secondary">BaseURL</Text>
              <Input style={{ marginTop: 6 }} value={form.baseUrl} placeholder="https://api.example.com/v1" onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))} />
            </div>
            <div>
              <Text type="secondary">优先级(大者优先路由)</Text>
              <InputNumber style={{ width: "100%", marginTop: 6 }} value={form.priority} onChange={(v) => setForm((f) => ({ ...f, priority: Number(v || 0) }))} />
            </div>
          </Space>
        )}
        {step === 1 && (
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            <div>
              <Text type="secondary">API Key(多行 = 多 Key)</Text>
              <Input.TextArea rows={3} style={{ marginTop: 6 }} value={form.apiKey} placeholder="sk-..." onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))} />
            </div>
            <Button block loading={testing} onClick={testConnection} icon={<i className="ri-pulse-line" />}>连接测试</Button>
            {testResult && (
              <Alert
                type={testResult.ok ? "success" : "error"}
                showIcon
                message={testResult.ok ? `连接成功 · ${testResult.latencyMs}ms` : "连接失败"}
                description={testResult.ok ? undefined : testResult.message}
              />
            )}
          </Space>
        )}
        {step === 2 && (
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            <Button block loading={fetching} onClick={fetchModels} icon={<i className="ri-download-cloud-line" />}>从上游拉取模型列表</Button>
            <div>
              <Text type="secondary">该渠道提供的模型(可勾选拉取结果或直接输入)</Text>
              <Select
                mode="tags"
                style={{ width: "100%", marginTop: 6 }}
                value={form.models}
                options={upstreamModels.map((m) => ({ value: m, label: m }))}
                placeholder="gpt-4o / deepseek-chat ..."
                onChange={(value) => setForm((f) => ({ ...f, models: value }))}
              />
            </div>
            <Alert type="warning" showIcon message="目录中不存在的模型名将自动创建为「待定价、未公开」目录项,定价前不会在用户模型中心展示。" />
          </Space>
        )}
        <Space style={{ marginTop: 24, width: "100%", justifyContent: "space-between" }}>
          <Button disabled={step === 0} onClick={() => setStep((s) => s - 1)}>上一步</Button>
          {step < 2 ? (
            <Button type="primary" disabled={!stepValid} onClick={() => setStep((s) => s + 1)}>下一步</Button>
          ) : (
            <Button type="primary" loading={saving} disabled={!stepValid} onClick={createChannel}>创建渠道</Button>
          )}
        </Space>
      </Drawer>
    </div>
  );
};

export default ModelGateway;
